import { v4 as uuidv4 } from 'uuid';
import { ValidationError, BookingTypeNotAllowedError } from '../utils/errors.js';
import { isTimeSlotAvailable } from '../utils/calculators.js';

/**
 * Represents a bookable rink within a green
 */
export class Rink {
  /**
   * Create a new Rink
   * @param {Object} data - Rink configuration
   * @param {string} [data._id] - Unique identifier (auto-generated if not provided)
   * @param {number} data.number - Rink number (1-12)
   * @param {Array<string>} [data.allowedBookingTypes] - Array of BookingType IDs that can be booked
   */
  constructor(data) {
    if (!data || typeof data !== 'object') {
      throw new ValidationError('Rink data is required');
    }

    if (typeof data.number !== 'number' || data.number < 1 || data.number > 12) {
      throw new ValidationError('Rink number must be between 1 and 12', 'number', data.number);
    }

    this._id = data._id || uuidv4();
    this.number = data.number;
    this.allowedBookingTypes = Array.isArray(data.allowedBookingTypes) 
      ? [...data.allowedBookingTypes] 
      : [];
  }

  /**
   * Update rink details
   * @param {Object} updates - Properties to update
   * @returns {Rink} This instance for chaining
   */
  update(updates) {
    if (!updates || typeof updates !== 'object') {
      throw new ValidationError('Updates object is required');
    }

    const allowedFields = ['number', 'allowedBookingTypes'];
    const updateKeys = Object.keys(updates);

    for (const key of updateKeys) {
      if (!allowedFields.includes(key)) {
        throw new ValidationError(`Field '${key}' cannot be updated`, key, updates[key]);
      }
    }

    // Validate number update
    if (updates.number !== undefined) {
      if (typeof updates.number !== 'number' || updates.number < 1 || updates.number > 12) {
        throw new ValidationError('Rink number must be between 1 and 12', 'number', updates.number);
      }
      this.number = updates.number;
    }

    // Validate allowedBookingTypes update
    if (updates.allowedBookingTypes !== undefined) {
      if (!Array.isArray(updates.allowedBookingTypes)) {
        throw new ValidationError('Allowed booking types must be an array', 'allowedBookingTypes', updates.allowedBookingTypes);
      }
      this.allowedBookingTypes = [...updates.allowedBookingTypes];
    }

    return this;
  }

  /**
   * Check availability for specific time
   * @param {Date} date - Date to check
   * @param {Date} start - Start time to check
   * @param {Date} end - End time to check
   * @param {Array} existingBookings - Existing bookings to check against
   * @param {string} excludeBookingId - Booking ID to exclude from check
   * @returns {boolean} True if available
   */
  isAvailable(date, start, end, existingBookings = [], excludeBookingId = null) {
    if (!date || !start || !end) {
      return false;
    }

    // Filter bookings for this rink and date
    const rinkBookings = existingBookings.filter(booking => 
      booking.rinkId === this._id &&
      booking.date.toDateString() === date.toDateString() &&
      booking.status !== 'cancelled'
    );

    return isTimeSlotAvailable(start, end, rinkBookings, excludeBookingId);
  }

  /**
   * Add a booking type to allowed types
   * @param {string} bookingTypeId - Booking type ID to add
   * @returns {Rink} This instance for chaining
   */
  addAllowedBookingType(bookingTypeId) {
    if (!bookingTypeId || typeof bookingTypeId !== 'string') {
      throw new ValidationError('Valid booking type ID is required', 'bookingTypeId', bookingTypeId);
    }

    if (!this.allowedBookingTypes.includes(bookingTypeId)) {
      this.allowedBookingTypes.push(bookingTypeId);
    }

    return this;
  }

  /**
   * Remove a booking type from allowed types
   * @param {string} bookingTypeId - Booking type ID to remove
   * @returns {Rink} This instance for chaining
   */
  removeAllowedBookingType(bookingTypeId) {
    if (!bookingTypeId || typeof bookingTypeId !== 'string') {
      throw new ValidationError('Valid booking type ID is required', 'bookingTypeId', bookingTypeId);
    }

    const index = this.allowedBookingTypes.indexOf(bookingTypeId);
    if (index > -1) {
      this.allowedBookingTypes.splice(index, 1);
    }

    return this;
  }

  /**
   * Check if booking type is allowed on this rink
   * @param {string} bookingTypeId - Booking type ID to check
   * @returns {boolean} True if booking type is allowed
   */
  isBookingTypeAllowed(bookingTypeId) {
    if (!bookingTypeId || typeof bookingTypeId !== 'string') {
      return false;
    }

    // If no restrictions, allow all booking types
    if (this.allowedBookingTypes.length === 0) {
      return true;
    }

    return this.allowedBookingTypes.includes(bookingTypeId);
  }

  /**
   * Validate if a booking can be made on this rink
   * @param {string} bookingTypeId - Booking type ID
   * @param {Date} date - Booking date
   * @param {Date} start - Start time
   * @param {Date} end - End time
   * @param {Array} existingBookings - Existing bookings
   * @param {string} excludeBookingId - Booking ID to exclude
   * @throws {BookingTypeNotAllowedError} When booking type is not allowed
   * @throws {ValidationError} When time slot is not available
   */
  validateBooking(bookingTypeId, date, start, end, existingBookings = [], excludeBookingId = null) {
    // Check if booking type is allowed
    if (!this.isBookingTypeAllowed(bookingTypeId)) {
      throw new BookingTypeNotAllowedError(
        `Booking type ${bookingTypeId} is not allowed on rink ${this.number}`,
        bookingTypeId,
        `rink-${this._id}`
      );
    }

    // Check availability
    if (!this.isAvailable(date, start, end, existingBookings, excludeBookingId)) {
      throw new ValidationError(
        `Rink ${this.number} is not available for the requested time slot`,
        'availability',
        { date, start, end }
      );
    }
  }

  /**
   * Get bookings for this rink within a date range
   * @param {Array} allBookings - All bookings to filter
   * @param {Date} startDate - Start of date range
   * @param {Date} endDate - End of date range
   * @returns {Array} Filtered bookings for this rink
   */
  getBookings(allBookings, startDate = null, endDate = null) {
    let rinkBookings = allBookings.filter(booking => booking.rinkId === this._id);

    if (startDate && endDate) {
      rinkBookings = rinkBookings.filter(booking => {
        const bookingDate = new Date(booking.date);
        return bookingDate >= startDate && bookingDate <= endDate;
      });
    }

    return rinkBookings.sort((a, b) => a.start - b.start);
  }

  /**
   * Get utilization statistics for this rink
   * @param {Array} allBookings - All bookings to analyze
   * @param {Date} startDate - Start of analysis period
   * @param {Date} endDate - End of analysis period
   * @returns {Object} Utilization statistics
   */
  getUtilizationStats(allBookings, startDate, endDate) {
    const rinkBookings = this.getBookings(allBookings, startDate, endDate)
      .filter(booking => booking.status === 'confirmed' || booking.status === 'completed');

    const totalMinutes = rinkBookings.reduce((sum, booking) => {
      return sum + Math.round((booking.end - booking.start) / (1000 * 60));
    }, 0);

    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const totalAvailableMinutes = totalDays * 12 * 60; // Assuming 12 hours per day

    return {
      rinkId: this._id,
      rinkNumber: this.number,
      totalBookings: rinkBookings.length,
      totalBookedMinutes: totalMinutes,
      totalAvailableMinutes,
      utilizationPercentage: (totalMinutes / totalAvailableMinutes) * 100,
      averageBookingDuration: rinkBookings.length > 0 ? totalMinutes / rinkBookings.length : 0
    };
  }

  /**
   * Convert to JSON string for persistence
   * @returns {string} JSON string representation
   */
  toJSON() {
    const data = {
      _id: this._id,
      number: this.number,
      allowedBookingTypes: [...this.allowedBookingTypes]
    };
    return JSON.stringify(data);
  }

  /**
   * Create Rink instance from JSON string
   * @param {string} jsonString - JSON string data
   * @returns {Rink} New Rink instance
   */
  static fromJSON(jsonString) {
    if (!jsonString || typeof jsonString !== 'string') {
      throw new ValidationError('Valid JSON string is required');
    }

    let jsonData;
    try {
      jsonData = JSON.parse(jsonString);
    } catch (error) {
      throw new ValidationError('Invalid JSON string format');
    }

    return new Rink({
      _id: jsonData._id,
      number: jsonData.number,
      allowedBookingTypes: jsonData.allowedBookingTypes || []
    });
  }
} 